<template>
  <b-modal
    ref="add-imei-modal"
    size="lg"
    centered
    hide-footer
    modal-class="imei-modal"
    body-class="imei-modal__body"
    header-class="imei-modal__header"
  >
    <template #modal-header="{ close }">
      <div class="imei-modal__head">
        <div class="imei-modal__head-icon">#</div>
        <div class="imei-modal__head-text">
          <h5 class="imei-modal__title">Danh sách IMEI sản phẩm</h5>
          <span v-if="productName" class="imei-modal__product-chip">
            <i class="fas fa-tag"></i>
            {{ productName }}
          </span>
        </div>
      </div>
      <button
        type="button"
        class="imei-modal__close"
        aria-label="Đóng"
        @click="close()"
      >
        <i class="fas fa-times"></i>
      </button>
    </template>

    <div class="imei-search">
      <label class="imei-search__label">Tìm IMEI</label>
      <div class="imei-search__field">
        <i class="fas fa-search imei-search__icon"></i>
        <input
          v-model="query"
          type="text"
          class="imei-search__input"
          placeholder="Nhập IMEI hoặc một phần của IMEI..."
          @input="onQueryInput"
        />
        <button
          v-if="query"
          type="button"
          class="imei-search__clear"
          @click="clearSearch"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="imei-stats">
      <span class="imei-chip">Tồn kho: {{ listItem.length }}</span>
      <span class="imei-chip">Kết quả: {{ filteredItems.length }}</span>
      <span class="imei-chip imei-chip--accent"
        >Đã chọn: {{ selectedCount }}</span
      >
    </div>

    <div class="imei-table">
      <div class="imei-table__head">
        <div class="imei-table__col-check"></div>
        <div class="imei-table__col-imei">IMEI</div>
        <div class="imei-table__col-price">GIÁ BÁN</div>
        <div class="imei-table__col-action"></div>
      </div>

      <div v-if="onLoading" class="imei-table__empty">
        <b-spinner small class="mr-2"></b-spinner> Đang tải dữ liệu...
      </div>

      <div v-else-if="!filteredItems.length" class="imei-table__empty">
        <i class="fas fa-inbox mb-2" style="font-size: 1.5rem"></i>
        <div>Không tìm thấy IMEI phù hợp</div>
      </div>

      <template v-else>
        <div
          v-for="item in pagedItems"
          :key="item.id"
          class="imei-table__row"
          :class="{ 'imei-table__row--selected': isSelected(item.id) }"
          @click="selectItem(item)"
        >
          <div class="imei-table__col-check" @click.stop>
            <b-form-radio
              :checked="selectedId"
              :value="item.id"
              @change="selectItem(item)"
            ></b-form-radio>
          </div>
          <div class="imei-table__col-imei">
            <span class="imei-code">{{ item.imeiCode }}</span>
          </div>
          <div class="imei-table__col-price">
            {{ formatPrice(item.sellingPrice) }} <span class="imei-unit">đ</span>
          </div>
          <div class="imei-table__col-action" @click.stop>
            <button
              type="button"
              class="imei-apply-btn"
              v-b-tooltip
              title="Chọn nhanh IMEI này"
              @click="applyImei(item.imeiCode)"
            >
              <i class="fas fa-check"></i>
            </button>
          </div>
        </div>
      </template>
    </div>

    <div class="imei-pager">
      <small class="text-muted"
        >Hiển thị <strong>{{ startItem }}–{{ endItem }}</strong> của
        <strong>{{ filteredItems.length }}</strong> IMEI</small
      >
      <b-pagination
        v-if="totalPages > 1"
        v-model="currentPage"
        :per-page="perPage"
        :total-rows="filteredItems.length"
        align="right"
        size="sm"
        class="mb-0"
      ></b-pagination>
    </div>

    <div class="imei-footer">
      <div class="imei-footer__summary">
        Đã chọn <strong>{{ selectedCount }}</strong> IMEI ·  Tổng tiền
        <strong>{{ formatPrice(selectedTotal) }}đ</strong>
      </div>
      <div class="imei-footer__actions">
        <b-button variant="light" class="imei-btn-cancel" @click="hideModal"
          >Hủy</b-button
        >
        <b-button
          variant="primary"
          class="imei-btn-confirm"
          :disabled="!selectedCount"
          @click="confirmSelection"
        >
          <i class="fas fa-check mr-1"></i> Xác nhận ({{ selectedCount }})
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { convertPrice } from '@/utils/common';

export default {
  props: ['billItemId', 'productName', 'storeId'],
  data() {
    return {
      listItem: [],
      selectedId: null,
      onLoading: false,
      query: '',
      debouncedTimer: null,
      currentPage: 1,
      perPage: 8,
    };
  },
  computed: {
    filteredItems() {
      const q = (this.query || '').trim().toLowerCase();
      const items = this.listItem.slice();
      if (!q) return items;
      return items.filter((it) =>
        String(it.imeiCode).toLowerCase().includes(q),
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage));
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredItems.slice(start, start + this.perPage);
    },
    startItem() {
      if (this.filteredItems.length === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endItem() {
      return Math.min(
        this.filteredItems.length,
        this.currentPage * this.perPage,
      );
    },
    selectedCount() {
      return this.selectedId != null ? 1 : 0;
    },
    selectedTotal() {
      const item = this.listItem.find((it) => it.id === this.selectedId);
      return item ? Number(item.sellingPrice || 0) : 0;
    },
  },
  watch: {
    filteredItems() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  methods: {
    convertPrice,
    formatPrice(value) {
      return convertPrice(Number(value || 0));
    },
    fetchData(productId) {
      this.onLoading = true;
      const paramQuery = {
        params: { productId, storeId: this.storeId },
      };
      ApiService.query('imeis/get-by-product', paramQuery)
        .then(({ data }) => {
          this.listItem = data.data.map((element) => ({
            id: element.id,
            imeiCode: element.imeiNo,
            originalPrice: element.originalPrice || 0,
            sellingPrice: element.sellingPrice || 0,
          }));
          this.onLoading = false;
        })
        .catch(() => {
          this.onLoading = false;
        });
    },
    showModal(productId) {
      this.query = '';
      this.currentPage = 1;
      this.selectedId = null;
      this.listItem = [];
      this.fetchData(productId);
      this.$refs['add-imei-modal'].show();
    },
    hideModal() {
      this.$refs['add-imei-modal'].hide();
    },
    isSelected(id) {
      return this.selectedId === id;
    },
    selectItem(item) {
      this.selectedId = this.selectedId === item.id ? null : item.id;
    },
    applyImei(imeiCode) {
      this.$emit('applyImei', imeiCode);
      this.hideModal();
    },
    confirmSelection() {
      const selected = this.listItem.find((it) => it.id === this.selectedId);
      if (!selected) return;
      this.$emit('applyImei', selected.imeiCode);
      this.hideModal();
    },
    onQueryInput() {
      if (this.debouncedTimer) clearTimeout(this.debouncedTimer);
      this.debouncedTimer = setTimeout(() => {
        this.currentPage = 1;
      }, 250);
    },
    clearSearch() {
      this.query = '';
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
/* Header */
.imei-modal__head {
  display: flex;
  align-items: center;
  gap: 14px;
}
.imei-modal__head-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #f1f3f9;
  color: #3f4254;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  flex-shrink: 0;
}
.imei-modal__head-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.imei-modal__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #181c32;
}
.imei-modal__product-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 9999px;
  background: #eef1f7;
  color: #3f4254;
  font-size: 0.82rem;
  font-weight: 600;
}
.imei-modal__product-chip i {
  font-size: 0.72rem;
  color: #7e8299;
}
.imei-modal__close {
  border: none;
  background: transparent;
  color: #a1a5b7;
  font-size: 1.05rem;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.imei-modal__close:hover {
  background: #f1f3f9;
  color: #3f4254;
}

/* Search */
.imei-search {
  margin-bottom: 16px;
}
.imei-search__label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 6px;
  display: block;
}
.imei-search__field {
  position: relative;
  display: flex;
  align-items: center;
}
.imei-search__icon {
  position: absolute;
  left: 14px;
  color: #a1a5b7;
  font-size: 0.9rem;
}
.imei-search__input {
  width: 100%;
  height: 44px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  padding: 0 40px 0 40px;
  font-size: 0.95rem;
  color: #181c32;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.imei-search__input:focus {
  outline: none;
  border-color: #b3cdfb;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.12);
}
.imei-search__clear {
  position: absolute;
  right: 12px;
  border: none;
  background: transparent;
  color: #a1a5b7;
  cursor: pointer;
}

/* Stat chips */
.imei-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.imei-chip {
  padding: 5px 12px;
  border-radius: 9999px;
  background: #f1f3f9;
  color: #5e6278;
  font-size: 0.8rem;
  font-weight: 600;
}
.imei-chip--accent {
  background: #e1f0ff;
  color: #2884ef;
}

/* Table */
.imei-table {
  border: 1px solid #eff2f5;
  border-radius: 12px;
  overflow: hidden;
}
.imei-table__head,
.imei-table__row {
  display: grid;
  grid-template-columns: 52px 1fr 180px 60px;
  align-items: center;
}
.imei-table__head {
  background: #f9fafc;
  border-bottom: 1px solid #eff2f5;
  padding: 12px 14px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #b5b5c3;
  text-transform: uppercase;
}
.imei-table__row {
  padding: 12px 14px;
  border-bottom: 1px solid #f4f6f9;
  cursor: pointer;
  transition: background 0.12s ease;
}
.imei-table__row:last-child {
  border-bottom: none;
}
.imei-table__row:hover {
  background: #f9fafc;
}
.imei-table__row--selected {
  background: #f4f8ff;
}
.imei-table__col-price {
  text-align: right;
  font-weight: 600;
  color: #181c32;
}
.imei-table__col-action {
  text-align: right;
}
.imei-code {
  font-family: 'Roboto Mono', 'SFMono-Regular', Menlo, Consolas, monospace;
  font-weight: 600;
  color: #181c32;
  font-size: 0.92rem;
}
.imei-unit {
  color: #a1a5b7;
  font-weight: 500;
  font-size: 0.8rem;
}
.imei-table__empty {
  text-align: center;
  padding: 40px 0;
  color: #a1a5b7;
}
.imei-apply-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e4e6ef;
  background: #fff;
  color: #b5b5c3;
  cursor: pointer;
  transition: all 0.15s ease;
}
.imei-apply-btn:hover {
  border-color: #0bb783;
  color: #fff;
  background: #0bb783;
}

/* Pager */
.imei-pager {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
}

/* Footer */
.imei-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eff2f5;
}
.imei-footer__summary {
  color: #5e6278;
  font-size: 0.9rem;
}
.imei-footer__summary strong {
  color: #181c32;
}
.imei-footer__actions {
  display: flex;
  gap: 10px;
}
.imei-btn-cancel {
  font-weight: 600;
  border-radius: 10px;
  padding: 0 20px;
}
.imei-btn-confirm {
  font-weight: 600;
  border-radius: 10px;
  padding: 0 22px;
}
</style>

<style>
.imei-modal .modal-content {
  border: none;
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(24, 28, 50, 0.18);
}
.imei-modal .imei-modal__header {
  align-items: flex-start;
  border-bottom: 1px solid #eff2f5;
  padding: 20px 24px;
}
.imei-modal .imei-modal__body {
  padding: 20px 24px 22px;
}
</style>
